import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import {
  DownloadSimple, Lightning, CircleNotch, LinkBreak, Clock,
  Image as ImageIcon, FilmStrip, MusicNotes, FilePdf, FileText, File as FileIcon,
} from "@phosphor-icons/react";

const KIND_ICON = { image: ImageIcon, video: FilmStrip, audio: MusicNotes, pdf: FilePdf, text: FileText, doc: FileText, other: FileIcon };
const KIND_COLOR = { image: "#00c9a7", video: "#a970ff", audio: "#f59e0b", pdf: "#ef4444", text: "#4dabf5", doc: "#4dabf5", other: "#8892a6" };
const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} МБ` : `${Math.max(1, Math.round(b / 1024))} КБ`);
const BASE = process.env.REACT_APP_BACKEND_URL;

export default function SharePage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${BASE}/api/share/${token}/info`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Ссылка недоступна"));
  }, [token]);

  const dl = `${BASE}/api/share/${token}/download`;
  const Icon = info ? KIND_ICON[info.kind] || FileIcon : FileIcon;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-10" data-testid="share-page">
      <div className="flex items-center gap-2 mb-8">
        <Lightning size={22} weight="fill" className="text-primary" />
        <span className="font-heading text-xl font-extrabold tracking-tight">LifeOS</span>
        <span className="text-xs text-muted-foreground mt-1">· Файлообменник</span>
      </div>

      <div className="glass-card w-full max-w-md p-8 text-center fade-in">
        {!info && !error && (
          <div className="flex flex-col items-center gap-3 py-8 text-muted-foreground" data-testid="share-loading">
            <CircleNotch size={28} className="animate-spin" /> Загружаем…
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center gap-3 py-6" data-testid="share-error">
            <LinkBreak size={44} weight="duotone" className="text-destructive" />
            <p className="text-sm font-semibold">{error}</p>
            <p className="text-xs text-muted-foreground">Попросите владельца отправить новую ссылку.</p>
          </div>
        )}

        {info && (
          <>
            {info.kind === "image" ? (
              <img src={`${dl}?preview=true`} alt={info.name}
                className="max-h-64 mx-auto rounded-xl border border-border/60 mb-5 object-contain" data-testid="share-preview" />
            ) : (
              <div className="w-20 h-20 rounded-2xl bg-secondary/60 flex items-center justify-center mx-auto mb-5">
                <Icon size={40} weight="duotone" style={{ color: KIND_COLOR[info.kind] }} />
              </div>
            )}
            <h1 className="text-lg font-bold break-all mb-1" data-testid="share-filename">{info.name}</h1>
            <p className="text-xs text-muted-foreground num mb-1">
              {fmtSize(info.size)}{info.owner_name ? ` · отправил(а) ${info.owner_name}` : ""}
            </p>
            {info.expires_at && (
              <p className="text-[11px] text-muted-foreground flex items-center justify-center gap-1 mb-4">
                <Clock size={11} /> Ссылка действует до {new Date(info.expires_at).toLocaleDateString("ru-RU", { day: "numeric", month: "long" })}
              </p>
            )}
            <a href={dl} download={info.name} data-testid="share-download-button">
              <Button className="w-full mt-3 h-11 text-sm font-semibold">
                <DownloadSimple size={18} className="mr-2" /> Скачать файл
              </Button>
            </a>
          </>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground mt-6">Передано через LifeOS — приложение для жизни</p>
    </div>
  );
}
